/**
 * Signup slot scheduling helpers.
 *
 * A signup event's slots used to be derived from one morning block and one evening block
 * repeated across every day of the event — fine for a uniform conference week, useless for a
 * round like the Sept 2026 TK/K conferences, where the times differ per day (one day has no
 * afternoon block), the cadence is wider than the slot itself (15-minute meetings every 30
 * minutes), the evening list is irregular, and each slot is either a Google Meet or in person.
 *
 * These functions are pure so the shapes above can be tested without a database. All times are
 * business-local clock times ("HH:MM" in, "HH:MM:SS" out to match the `TIME` columns); nothing
 * here goes near `Date`, so a parent's device timezone can't shift a slot.
 */

export type SignupMeetingMode = 'in_person' | 'google_meet';

export const SIGNUP_MEETING_MODES: readonly SignupMeetingMode[] = ['in_person', 'google_meet'] as const;

const MEETING_MODE_LABELS: Record<SignupMeetingMode, string> = {
  in_person: 'In person',
  google_meet: 'Google Meet',
};

/** Human label for a slot's meeting mode. Unknown/legacy values render as an em dash. */
export function meetingModeLabel(mode: string | null | undefined): string {
  const normalized = normalizeMeetingMode(mode);
  return normalized ? MEETING_MODE_LABELS[normalized] : '—';
}

/**
 * Narrows a raw column value to a known mode. Slots created before per-slot modes existed
 * carry `null`, and those keep behaving exactly as they did — no badge, in-person registration.
 */
export function normalizeMeetingMode(value: string | null | undefined): SignupMeetingMode | null {
  return value === 'in_person' || value === 'google_meet' ? value : null;
}

export interface SlotDraft {
  /** ISO date, "YYYY-MM-DD". */
  date: string;
  /** "HH:MM" or "HH:MM:SS". */
  startTime: string;
  endTime: string;
  meetingMode: SignupMeetingMode | null;
}

export interface SignupSlotRow {
  event_id: string;
  slot_date: string;
  start_time: string;
  end_time: string;
  meeting_mode: SignupMeetingMode | null;
  is_available: boolean;
}

export interface TimeBlock {
  startTime: string;
  endTime: string;
  meetingMode: SignupMeetingMode | null;
}

export interface DailyBlockInput {
  startDate: string;
  endDate: string;
  slotDurationMinutes: number;
  /** How often a slot starts. Defaults to the slot duration (back-to-back slots). */
  slotIntervalMinutes?: number;
  blocks: (TimeBlock | null | undefined)[];
}

export function timeToMinutes(time: string): number {
  const [hh, mm] = time.split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  return hh * 60 + mm;
}

export function minutesToTime(minutes: number): string {
  const clamped = Math.max(0, Math.round(minutes));
  const hh = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
}

/** "HH:MM:SS" → "HH:MM", for `<input type="time">` values. */
export function toHHMM(time: string): string {
  const [hh = '', mm = ''] = time.split(':');
  return `${hh}:${mm}`;
}

/** Every date from start to end inclusive, as ISO strings. Empty if the range is invalid. */
export function datesInRange(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate || startDate > endDate) return [];

  const dates: string[] = [];
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  if ([sy, sm, sd, ey, em, ed].some((n) => !Number.isFinite(n))) return [];

  // Noon avoids any DST edge shifting the date when stepping a day at a time.
  const cursor = new Date(sy, sm - 1, sd, 12);
  const last = new Date(ey, em - 1, ed, 12);
  while (cursor <= last && dates.length < 366) {
    dates.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`
    );
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/** Repeats the given time blocks across every day of the range. */
export function buildDailyBlockSlots(input: DailyBlockInput): SlotDraft[] {
  const duration = input.slotDurationMinutes;
  if (!Number.isFinite(duration) || duration <= 0) return [];

  const interval = input.slotIntervalMinutes && input.slotIntervalMinutes > 0
    ? input.slotIntervalMinutes
    : duration;

  const drafts: SlotDraft[] = [];
  for (const date of datesInRange(input.startDate, input.endDate)) {
    for (const block of input.blocks) {
      if (!block?.startTime || !block?.endTime) continue;

      const blockStart = timeToMinutes(block.startTime);
      const blockEnd = timeToMinutes(block.endTime);
      if (!Number.isFinite(blockStart) || !Number.isFinite(blockEnd)) continue;

      // A slot has to finish inside its block, so a block shorter than one slot yields nothing.
      for (let start = blockStart; start + duration <= blockEnd; start += interval) {
        drafts.push({
          date,
          startTime: minutesToTime(start),
          endTime: minutesToTime(start + duration),
          meetingMode: block.meetingMode ?? null,
        });
      }
    }
  }

  return sortSlotDrafts(drafts);
}

export function sortSlotDrafts(drafts: SlotDraft[]): SlotDraft[] {
  return [...drafts].sort((a, b) => {
    const byDate = a.date.localeCompare(b.date);
    if (byDate !== 0) return byDate;
    return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
  });
}

/**
 * Drops incomplete rows and collapses duplicates, keeping the first of each
 * (date, start time) pair — the same key the `signup_slots` unique constraint uses, so an
 * admin who adds the same time twice gets one slot instead of a failed insert.
 */
export function normalizeSlotDrafts(drafts: SlotDraft[]): SlotDraft[] {
  const seen = new Set<string>();
  const kept: SlotDraft[] = [];

  for (const draft of drafts) {
    if (!draft.date || !draft.startTime || !draft.endTime) continue;
    if (timeToMinutes(draft.endTime) <= timeToMinutes(draft.startTime)) continue;

    const key = `${draft.date}T${toHHMM(draft.startTime)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    kept.push({
      date: draft.date,
      startTime: minutesToTime(timeToMinutes(draft.startTime)),
      endTime: minutesToTime(timeToMinutes(draft.endTime)),
      meetingMode: normalizeMeetingMode(draft.meetingMode),
    });
  }

  return sortSlotDrafts(kept);
}

export function slotDraftsToRows(eventId: string, drafts: SlotDraft[]): SignupSlotRow[] {
  return normalizeSlotDrafts(drafts).map((draft) => ({
    event_id: eventId,
    slot_date: draft.date,
    start_time: draft.startTime,
    end_time: draft.endTime,
    meeting_mode: draft.meetingMode,
    is_available: true,
  }));
}

/** Groups drafts by date, in date order, for the per-day editor. */
export function groupDraftsByDate(drafts: SlotDraft[]): [string, SlotDraft[]][] {
  const byDate = new Map<string, SlotDraft[]>();
  for (const draft of sortSlotDrafts(drafts)) {
    const list = byDate.get(draft.date);
    if (list) list.push(draft);
    else byDate.set(draft.date, [draft]);
  }
  return [...byDate.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

/** Replaces every other day's slots with a copy of `sourceDate`'s, keeping that day as-is. */
export function copyDayToDates(drafts: SlotDraft[], sourceDate: string, targetDates: string[]): SlotDraft[] {
  const source = drafts.filter((d) => d.date === sourceDate);
  const targets = new Set(targetDates.filter((date) => date !== sourceDate));
  const kept = drafts.filter((d) => !targets.has(d.date));

  const copies: SlotDraft[] = [];
  for (const date of targets) {
    for (const draft of source) {
      copies.push({ ...draft, date });
    }
  }

  return sortSlotDrafts([...kept, ...copies]);
}

export type ScheduleMode = 'daily' | 'custom';

export interface SignupScheduleValue {
  scheduleMode: ScheduleMode;
  startDate: string;
  endDate: string;
  slotDurationMinutes: number;
  slotIntervalMinutes: number;
  morningStartTime: string;
  morningEndTime: string;
  morningMeetingMode: SignupMeetingMode | '';
  eveningStartTime: string;
  eveningEndTime: string;
  eveningMeetingMode: SignupMeetingMode | '';
  customSlots: SlotDraft[];
}

/** The blocks the "same times every day" mode repeats, in the order they run. */
export function blocksFromValue(value: SignupScheduleValue) {
  return [
    {
      startTime: value.morningStartTime,
      endTime: value.morningEndTime,
      meetingMode: normalizeMeetingMode(value.morningMeetingMode),
    },
    {
      startTime: value.eveningStartTime,
      endTime: value.eveningEndTime,
      meetingMode: normalizeMeetingMode(value.eveningMeetingMode),
    },
  ];
}

/** The slots an event would get from the current schedule settings, whichever mode is chosen. */
export function slotsFromSchedule(value: SignupScheduleValue): SlotDraft[] {
  if (value.scheduleMode === 'custom') {
    // A day outside the event's date range can't be booked, so don't create slots for one.
    const inRange = new Set(datesInRange(value.startDate, value.endDate));
    return normalizeSlotDrafts(value.customSlots.filter((slot) => inRange.has(slot.date)));
  }

  return buildDailyBlockSlots({
    startDate: value.startDate,
    endDate: value.endDate,
    slotDurationMinutes: value.slotDurationMinutes,
    slotIntervalMinutes: value.slotIntervalMinutes,
    blocks: blocksFromValue(value),
  });
}
